import React, { createContext, useState, useContext, useEffect } from 'react';
import { authService } from '../services/api';
import { STORAGE_KEYS } from '../config/constants';

// Create the auth context
export const AuthContext = createContext();

// Auth provider component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true); // For initial auth check
  const [otpLoading, setOtpLoading] = useState(false); // For OTP operations
  const [error, setError] = useState(null);

  // Check for stored token on startup
  useEffect(() => {
    const checkToken = async () => {
      try {
        console.log('[AuthContext] Checking for stored token...');
        
        const storedToken = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
        const storedUser = localStorage.getItem(STORAGE_KEYS.USER_DATA);
        
        if (storedToken && storedUser) {
          console.log('[AuthContext] Found stored auth data, validating...');
          setToken(storedToken);
          
          try {
            // Validate token by fetching user data
            const response = await authService.getMe();
            if (response.data && response.data.success) {
              console.log('[AuthContext] Token valid, setting user data');
              const userData = response.data.data;
              setUser(userData);
              // Update stored user data
              localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(userData));
            }
          } catch (error) {
            console.error('[AuthContext] Token validation failed:', error);
            // Clear invalid token
            await logout();
          }
        } else {
          console.log('[AuthContext] No stored token found');
        }
      } catch (error) {
        console.error('[AuthContext] Error checking auth state:', error);
        await logout();
      } finally {
        setLoading(false);
      }
    };

    checkToken();
  }, []);

  // Send OTP for login
  const sendLoginOTP = async (mobile) => {
    try {
      setOtpLoading(true);
      setError(null);
      
      const response = await authService.sendLoginOTP(mobile);
      console.log('[AuthContext] Login OTP sent successfully');
      
      return { success: true, data: response.data };
    } catch (error) {
      console.error('[AuthContext] Send login OTP error:', error);
      
      // Handle specific error cases for login
      let errorMessage = 'Failed to send OTP';
      
      if (error.response?.status === 404 || 
          error.response?.data?.message?.toLowerCase().includes('not found') ||
          error.response?.data?.message?.toLowerCase().includes('not registered') ||
          error.response?.data?.message?.toLowerCase().includes('user not found')) {
        errorMessage = 'The mobile number is not registered with us. Please signup.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setOtpLoading(false);
    }
  };

  // Send OTP for signup
  const sendSignupOTP = async (mobile, name) => {
    try {
      setOtpLoading(true);
      setError(null);
      
      const response = await authService.sendSignupOTP(mobile, name);
      console.log('[AuthContext] Signup OTP sent successfully');
      
      return { success: true, data: response.data };
    } catch (error) {
      console.error('[AuthContext] Send signup OTP error:', error);
      
      // Handle specific error cases for signup
      let errorMessage = 'Failed to send OTP';
      
      if (error.response?.status === 409 || 
          error.response?.status === 400 || 
          error.response?.data?.message?.toLowerCase().includes('already registered') ||
          error.response?.data?.message?.toLowerCase().includes('already exists') ||
          error.response?.data?.message?.toLowerCase().includes('user already exists')) {
        errorMessage = 'The mobile number you entered is already registered with us, please try with another mobile number. Thank you.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setOtpLoading(false);
    }
  };

  // Verify OTP for login/signup
  const verifyOTP = async (mobile, otp, type = 'login') => {
    try {
      setOtpLoading(true);
      setError(null);
      
      const response = await authService.verifyOTP(mobile, otp, type);
      
      if (response.data && response.data.success) {
        const { token: newToken, data: userData } = response.data;
        
        console.log('[AuthContext] OTP verified successfully');
        
        // Store auth data
        localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, newToken);
        localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(userData));
        
        // Update state
        setToken(newToken);
        setUser(userData);
        
        return { success: true, user: userData };
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('[AuthContext] Verify OTP error:', error);
      const errorMessage = error.response?.data?.message || 'Invalid OTP';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setOtpLoading(false);
    }
  };

  // Resend OTP
  const resendOTP = async (mobile, type = 'login') => {
    try {
      setOtpLoading(true);
      setError(null);
      
      const response = await authService.resendOTP(mobile, type);
      console.log('[AuthContext] OTP resent successfully');
      
      return { success: true, data: response.data };
    } catch (error) {
      console.error('[AuthContext] Resend OTP error:', error);
      
      // Handle specific error cases for resend OTP
      let errorMessage = 'Failed to resend OTP';
      
      if (type === 'login' && (error.response?.status === 404 || 
          error.response?.data?.message?.toLowerCase().includes('not found') ||
          error.response?.data?.message?.toLowerCase().includes('not registered'))) {
        errorMessage = 'The mobile number is not registered with us. Please signup.';
      } else if (type === 'signup' && (error.response?.status === 409 || 
          error.response?.data?.message?.toLowerCase().includes('already registered') ||
          error.response?.data?.message?.toLowerCase().includes('already exists'))) {
        errorMessage = 'The mobile number you entered is already registered with us, please try with another mobile number. Thank you.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setOtpLoading(false);
    }
  };

  // Logout user
  const logout = async () => {
    try {
      console.log('[AuthContext] Logging out user...');
      
      // Clear storage
      localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
      localStorage.removeItem(STORAGE_KEYS.USER_DATA);
      localStorage.removeItem(STORAGE_KEYS.CART_ITEMS);
      
      // Clear state
      setToken(null);
      setUser(null);
      setError(null);
      
      console.log('[AuthContext] User logged out successfully');
    } catch (error) {
      console.error('[AuthContext] Logout error:', error);
    }
  };

  // Update user details
  const updateUserDetails = async (details) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await authService.updateDetails(details);
      
      if (response.data && response.data.success) {
        const updatedUser = response.data.data;
        setUser(updatedUser);
        localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(updatedUser));
        
        console.log('[AuthContext] User details updated successfully');
        return { success: true, user: updatedUser };
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('[AuthContext] Update user details error:', error);
      const errorMessage = error.response?.data?.message || 'Failed to update details';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  // Helper function to check if user is authenticated
  const isAuthenticated = Boolean(token && user);

  // Helper function to get user name safely
  const getUserName = () => {
    if (!user) return 'Guest';
    return user.name || user.mobile || 'User';
  };

  const value = {
    user,
    token,
    loading,
    otpLoading,
    error,
    isAuthenticated,
    sendLoginOTP,
    sendSignupOTP,
    verifyOTP,
    resendOTP,
    logout,
    updateUserDetails,
    getUserName,
    setError // Allow manual error clearing
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 